import React, { FC } from 'react';

import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import type {
  IWorkshiftListFormValues,
} from '../../../../../hooks/Admin/PharmacyList/useListOfWorkshifts';

import {
  ListOfWorkshiftsTabTypes,
  ReplacementType,
} from '../../../../../helpers/Constants';
import CustomDateRange from '../../../../common/CustomDateRange';
import FilterSubmitButtons from '../../../../common/FilterSubmitButtons';
import SelectAllHeader from '../../../../DynamicTable/SelectAllHeader';
import CustomDropdown from '../../../../common/CustomDropdown';
import CustomButton from '../../../../common/CustomButton';
import strings from '../../../../../localization';
import PendingTabs from '../../../PendingTabs';

import ListOfWorkshiftsInfo from './ListOfWorkshiftsInfo';
import { tabConfig, workshiftStatuses, workshiftTypes } from './helper';
import styles from './style';

interface IListOfWorkshiftsHeader {
  formik: FormikProps<IWorkshiftListFormValues>;
  setSelected: React.Dispatch<React.SetStateAction<ListOfWorkshiftsTabTypes>>;
  selectedTab: ListOfWorkshiftsTabTypes;
  selectAll: boolean;
  handleSelectAll: (toBeSet: boolean) => void;
  handleDelete: () => void;
  selectableData: Array<string>;
  handleReset: () => void;
  canUpdateMultiple: boolean;
  hasMultiDaySelected: boolean;
  handleUpdate: () => void;
  selectedCount: number;
}

const ListOfWorkshiftsHeader: FC<IListOfWorkshiftsHeader> = ({
  selectedTab,
  formik,
  setSelected,
  selectAll,
  handleSelectAll,
  handleDelete,
  selectableData,
  handleReset,
  canUpdateMultiple,
  hasMultiDaySelected,
  handleUpdate,
  selectedCount,
}) => {
  const isPast = selectedTab === ListOfWorkshiftsTabTypes.pastWorkshifts;

  const handleTabClick = (tab: ListOfWorkshiftsTabTypes) => {
    setSelected(tab);
  };

  const statuses = workshiftStatuses(isPast);
  const noSelectableData = selectableData?.length === 0;
  const updateTooltip = hasMultiDaySelected
    ? strings.multiDayShiftsCannotBeUpdated
    : '';

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Box sx={styles.root}>
        <Typography sx={styles.title}>{strings.listOfWorkshifts}</Typography>
      </Box>

      <ListOfWorkshiftsInfo />

      <PendingTabs
        selectedTab={selectedTab}
        tabConfig={tabConfig(handleTabClick)}
      />
      <Grid container lg={12} alignItems="center" xs={12} wrap="nowrap">
        <CustomDateRange
          xs={2}
          startName="startDate"
          endName="endDate"
          formik={formik}
          preventPast={!isPast}
          preventFuture={isPast}
        />
        <Grid xs={2}>
          <CustomDropdown
            formik={formik}
            label={strings.workshiftType}
            name="shiftType"
            customClass={{ mr: 1 }}
            menuItems={workshiftTypes()}
          />
        </Grid>
        <Grid xs={2}>
          <CustomDropdown
            formik={formik}
            label={strings.replacementType}
            name="replacementTypes"
            multiple
            customClass={{ mr: 1 }}
            menuItems={ReplacementType()}
          />
        </Grid>
        <Grid xs={2}>
          <CustomDropdown
            formik={formik}
            label={strings.filterBy}
            name="status"
            menuItems={statuses}
          />
        </Grid>
        <FilterSubmitButtons handleReset={handleReset} />
      </Grid>

      {!noSelectableData && !isPast && (
        <SelectAllHeader
          selectAll={selectAll}
          handleSelectAll={handleSelectAll}
          showDelete
          handleDelete={handleDelete}
          CustomIcon={(
            <Tooltip title={updateTooltip}>
              <Box>
                <CustomButton
                  type="button"
                  variant="outlined"
                  fullWidth={false}
                  disabled={!canUpdateMultiple}
                  label={strings.formatString(
                    strings.updateRecordsCount,
                    selectedCount,
                  ) as string}
                  onClick={handleUpdate}
                  customClass={styles.updateButton}
                />
              </Box>
            </Tooltip>
          )}
        />
      )}
    </Box>
  );
};

export default ListOfWorkshiftsHeader;
